import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort, type SortState } from '../../components/SortableTh';
import {
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
  NumberInput,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { MonthlySummaryReport } from '../../types/reports';
import { Office } from '../../types/office';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportMonthlySummary } from './exportReports';

const totalsCellClass = 'sticky bottom-0 z-10 bg-surface-muted px-3 py-2';
const totalsNumClass = `${totalsCellClass} whitespace-nowrap text-right tabular-nums`;

export function MonthlySummaryPage() {
  const labels = t().reports;
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [report, setReport] = useState<MonthlySummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chargeSort = useTableSort('date');
  const paymentSort = useTableSort('date');
  const chargeRows = useMemo(
    () => (report ? sortItems(report.charges, chargeSort.sort, chargeSortGetters) : []),
    [report, chargeSort.sort],
  );
  const paymentRows = useMemo(
    () =>
      report
        ? sortItems(report.payments, paymentSort.sort, paymentMonthSortGetters)
        : [],
    [report, paymentSort.sort],
  );

  useEffect(() => {
    void api.getOffices(false).then(setOffices);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getMonthlySummary(
          month,
          year,
          officeId || undefined,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month, year, officeId]);

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    exportMonthlySummary({
      report,
      charges: chargeRows,
      payments: paymentRows,
      officeName:
        offices.find((office) => String(office.id) === officeId)?.name ??
        t().customers.filterAll,
      format,
    });
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <FilterField label={labels.month}>
          <Select
            value={month}
            size="sm"
            showClearButton={false}
            options={t().common.months.map((name, index) => ({
              value: index + 1,
              label: name,
            }))}
            onChange={(e) => setMonth(Number(e.target.value))}
          />
        </FilterField>
        <FilterField label={labels.year}>
          <NumberInput
            value={year}
            allowDecimals={false}
            showClearButton={false}
            size="sm"
            onChange={(e) => {
              if (e.target.value != null) setYear(e.target.value);
            }}
          />
        </FilterField>
        <FilterField label={t().nav.office}>
          <Select
            value={officeId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...offices.map((office) => ({
                value: String(office.id),
                label: office.active
                  ? office.name
                  : `${office.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setOfficeId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading || !report ? (
        <LoadingSpinner message={t().common.loading} />
      ) : (
        <>
          <div className="grid shrink-0 gap-4 md:grid-cols-2">
            <SummaryBox
              title={labels.chargeTotal}
              invoice={report.summary.charges.invoice}
              cash={report.summary.charges.cash}
              total={report.summary.charges.total}
              invoiceLabel={labels.chargeInvoice}
              cashLabel={labels.chargeCash}
            />
            <SummaryBox
              title={labels.paymentTotal}
              invoice={report.summary.payments.invoice}
              cash={report.summary.payments.cash}
              total={report.summary.payments.total}
              invoiceLabel={labels.paymentInvoice}
              cashLabel={labels.paymentCash}
            />
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 xl:flex-row">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 xl:basis-0">
              <h3 className="shrink-0 text-sm font-semibold text-ink">
                {labels.monthCharges}
              </h3>
              <DataTablePanel className="min-h-0 min-w-0 flex-1">
                <TableCharges
                  rows={chargeRows}
                  totals={report.summary.charges}
                  sort={chargeSort.sort}
                  onSort={chargeSort.toggleSort}
                />
              </DataTablePanel>
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 xl:basis-0">
              <h3 className="shrink-0 text-sm font-semibold text-ink">
                {labels.monthPayments}
              </h3>
              <DataTablePanel className="min-h-0 min-w-0 flex-1">
                <TablePayments
                  rows={paymentRows}
                  totals={report.summary.payments}
                  sort={paymentSort.sort}
                  onSort={paymentSort.toggleSort}
                />
              </DataTablePanel>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}

function SummaryBox({
  title,
  invoice,
  cash,
  total,
  invoiceLabel,
  cashLabel,
}: {
  title: string;
  invoice: string;
  cash: string;
  total: string;
  invoiceLabel: string;
  cashLabel: string;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between">
          <span>{invoiceLabel}</span>
          <span className="tabular-nums">{formatMoney(invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{cashLabel}</span>
          <span className="tabular-nums">{formatMoney(cash)}</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(total)}</span>
        </div>
      </div>
    </Card>
  );
}

const chargeSortGetters: Record<
  string,
  (
    row: MonthlySummaryReport['charges'][number],
  ) => string | number | boolean | null | undefined
> = {
  date: (r) => r.date,
  customer: (r) => r.customer.name,
  invoiceNo: (r) => r.invoiceNo,
  category: (r) => r.category?.name,
  invoice: (r) => r.totals.invoice,
  cash: (r) => r.totals.cash,
  total: (r) => r.totals.total,
};

function TableCharges({
  rows,
  totals,
  sort,
  onSort,
}: {
  rows: MonthlySummaryReport['charges'];
  totals: MonthlySummaryReport['summary']['charges'];
  sort: SortState;
  onSort: (key: string) => void;
}) {

  if (rows.length === 0) {
    return <ListEmptyState title={t().common.empty} />;
  }
  return (
    <table className="w-max min-w-full text-sm">
      <thead className={dataTableHeadClass}>
        <tr>
          <SortableTh sortKey="date" sort={sort} onSort={onSort}>
            {t().common.date}
          </SortableTh>
          <SortableTh sortKey="customer" sort={sort} onSort={onSort}>
            {t().common.customer}
          </SortableTh>
          <SortableTh sortKey="invoiceNo" sort={sort} onSort={onSort}>
            {t().invoices.invoiceNo}
          </SortableTh>
          <SortableTh sortKey="category" sort={sort} onSort={onSort}>
            {t().invoices.category}
          </SortableTh>
          <SortableTh
            sortKey="invoice"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().financial.invoice}
          </SortableTh>
          <SortableTh
            sortKey="cash"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().financial.cash}
          </SortableTh>
          <SortableTh
            sortKey="total"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().common.total}
          </SortableTh>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-line">
            <td className="whitespace-nowrap px-3 py-2">
              {formatDate(row.date)}
            </td>
            <td className="max-w-[10rem] px-3 py-2">
              <Link
                to={`/customers/${row.customer.id}`}
                className="block truncate text-brand hover:underline"
                title={row.customer.name}
              >
                {row.customer.name}
              </Link>
            </td>
            <td className="whitespace-nowrap px-3 py-2">
              <Link
                to={`/invoices/${row.id}`}
                className="text-brand hover:underline"
              >
                {row.invoiceNo}
              </Link>
            </td>
            <td className="max-w-[8rem] px-3 py-2">
              <span className="block truncate" title={row.category?.name || undefined}>
                {row.category?.name || '—'}
              </span>
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
              {formatMoney(row.totals.invoice)}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
              {formatMoney(row.totals.cash)}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">
              {formatMoney(row.totals.total)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot className={dataTableFootClass}>
        <tr>
          <td className={totalsCellClass} colSpan={4}>
            {t().common.total}
          </td>
          <td className={totalsNumClass}>{formatMoney(totals.invoice)}</td>
          <td className={totalsNumClass}>{formatMoney(totals.cash)}</td>
          <td className={totalsNumClass}>{formatMoney(totals.total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

const paymentMonthSortGetters: Record<
  string,
  (
    row: MonthlySummaryReport['payments'][number],
  ) => string | number | boolean | null | undefined
> = {
  date: (r) => r.date,
  customer: (r) => r.customer.name,
  invoiceNo: (r) => r.invoice?.invoiceNo,
  invoice: (r) => r.totals.invoice,
  cash: (r) => r.totals.cash,
  total: (r) => r.totals.total,
};

function TablePayments({
  rows,
  totals,
  sort,
  onSort,
}: {
  rows: MonthlySummaryReport['payments'];
  totals: MonthlySummaryReport['summary']['payments'];
  sort: SortState;
  onSort: (key: string) => void;
}) {

  if (rows.length === 0) {
    return <ListEmptyState title={t().common.empty} />;
  }
  return (
    <table className="w-max min-w-full text-sm">
      <thead className={dataTableHeadClass}>
        <tr>
          <SortableTh sortKey="date" sort={sort} onSort={onSort}>
            {t().common.date}
          </SortableTh>
          <SortableTh sortKey="customer" sort={sort} onSort={onSort}>
            {t().common.customer}
          </SortableTh>
          <SortableTh sortKey="invoiceNo" sort={sort} onSort={onSort}>
            {t().invoices.invoiceNo}
          </SortableTh>
          <SortableTh
            sortKey="invoice"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().financial.invoice}
          </SortableTh>
          <SortableTh
            sortKey="cash"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().financial.cash}
          </SortableTh>
          <SortableTh
            sortKey="total"
            sort={sort}
            onSort={onSort}
            align="right"
          >
            {t().common.total}
          </SortableTh>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-line">
            <td className="whitespace-nowrap px-3 py-2">
              {formatDate(row.date)}
            </td>
            <td className="max-w-[10rem] px-3 py-2">
              <Link
                to={`/customers/${row.customer.id}`}
                className="block truncate text-brand hover:underline"
                title={row.customer.name}
              >
                {row.customer.name}
              </Link>
            </td>
            <td className="whitespace-nowrap px-3 py-2">
              {row.invoice ? (
                <Link
                  to={`/invoices/${row.invoice.id}`}
                  className="text-brand hover:underline"
                >
                  {row.invoice.invoiceNo}
                </Link>
              ) : (
                '—'
              )}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
              {formatMoney(row.totals.invoice)}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
              {formatMoney(row.totals.cash)}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">
              {formatMoney(row.totals.total)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot className={dataTableFootClass}>
        <tr>
          <td className={totalsCellClass} colSpan={3}>
            {t().common.total}
          </td>
          <td className={totalsNumClass}>{formatMoney(totals.invoice)}</td>
          <td className={totalsNumClass}>{formatMoney(totals.cash)}</td>
          <td className={totalsNumClass}>{formatMoney(totals.total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}
